<div class="edit-profile-container">
  <div class="loading-overlay" *ngIf="loading">
    <div class="custom-loading-spinner"></div>
    <div class="loading-content">
      <h3 class="loading-title">Loading Profile...</h3>
      <p class="loading-text">Preparing your information</p>
    </div>
  </div>

  <div class="profile-content" *ngIf="!loading">
    <div class="profile-header-section">
      <div class="avatar-section">
        <div class="avatar-container">
          <img
            class="profile-avatar"
            [src]="currentUser.profile_image || defaultImage"
            alt="Profile picture"
          />
          <button
            type="button"
            class="avatar-edit-btn"
            (click)="pickPhoto()"
            aria-label="Change profile picture"
          >
            <span class="edit-icon"
              ><lucide-icon name="edit" [size]="16"></lucide-icon
            ></span>
          </button>
          <input
            #fileInput
            type="file"
            accept="image/*"
            hidden
            (change)="onImageSelected($event)"
          />
        </div>
        <div class="photo-options">
          <button type="button" class="photo-option-btn" (click)="takePhoto()">
            <span class="btn-icon"
              ><lucide-icon name="smartphone" [size]="16"></lucide-icon
            ></span>
            <span class="btn-text">Take Photo</span>
          </button>
          <button
            type="button"
            class="photo-option-btn"
            (click)="selectFromGallery()"
          >
            <span class="btn-icon"
              ><lucide-icon name="image" [size]="16"></lucide-icon
            ></span>
            <span class="btn-text">Gallery</span>
          </button>
          <button
            type="button"
            class="photo-option-btn"
            (click)="triggerFileInput()"
          >
            <span class="btn-icon"
              ><lucide-icon name="search" [size]="16"></lucide-icon
            ></span>
            <span class="btn-text">Browse</span>
          </button>
        </div>
        <div class="avatar-info">
          <h2 class="profile-title">Edit Profile</h2>
          <p class="profile-subtitle">Update your personal information</p>
        </div>
      </div>
    </div>

    <div class="form-section">
      <div class="section-card">
        <h3 class="section-title">Personal Information</h3>

        <form
          [formGroup]="profileForm"
          (ngSubmit)="onSave()"
          class="profile-form"
        >
          <div class="form-grid">
            <div class="form-group">
              <label class="form-label" for="username">Username</label>
              <div class="input-wrapper">
                <input
                  id="username"
                  class="form-input disabled"
                  formControlName="username"
                  readonly
                />
                <span class="input-icon"
                  ><lucide-icon name="user" [size]="16"></lucide-icon
                ></span>
              </div>
            </div>

            <div class="form-group">
              <label class="form-label" for="name">First Name</label>
              <div class="input-wrapper">
                <input
                  id="name"
                  class="form-input"
                  formControlName="name"
                  placeholder="Enter your first name"
                />
                <span class="input-icon"
                  ><lucide-icon name="edit" [size]="16"></lucide-icon
                ></span>
              </div>
              <small *ngIf="isInvalid('name')" class="error-message">
                First name is required
              </small>
            </div>

            <div class="form-group">
              <label class="form-label" for="last_name">Last Name</label>
              <div class="input-wrapper">
                <input
                  id="last_name"
                  class="form-input"
                  formControlName="last_name"
                  placeholder="Enter your last name"
                />
                <span class="input-icon"
                  ><lucide-icon name="edit" [size]="16"></lucide-icon
                ></span>
              </div>
              <small *ngIf="isInvalid('last_name')" class="error-message">
                Last name is required
              </small>
            </div>

            <div class="form-group">
              <label class="form-label" for="email">Email Address</label>
              <div class="input-wrapper">
                <input
                  id="email"
                  class="form-input"
                  formControlName="email"
                  type="email"
                  placeholder="Enter your email"
                />
                <span class="input-icon"
                  ><lucide-icon name="mail" [size]="16"></lucide-icon
                ></span>
              </div>
              <small *ngIf="isInvalid('email')" class="error-message">
                Valid email is required
              </small>
            </div>

            <div class="form-group">
              <label class="form-label" for="phone">Phone Number</label>
              <div class="input-wrapper">
                <input
                  id="phone"
                  class="form-input"
                  formControlName="phone"
                  placeholder="Enter your phone number"
                />
                <span class="input-icon"
                  ><lucide-icon name="phone" [size]="16"></lucide-icon
                ></span>
              </div>
              <small *ngIf="isInvalid('phone')" class="error-message">
                Phone number is required
              </small>
            </div>

            <div class="form-group">
              <label class="form-label" for="gender">Gender</label>
              <div
                class="custom-dropdown"
                [class.open]="genderDropdownOpen"
                [class.error]="isInvalid('gender')"
              >
                <div class="dropdown-trigger" (click)="toggleGenderDropdown()">
                  <span class="dropdown-value">{{
                    getSelectedGenderLabel()
                  }}</span>
                  <div class="dropdown-arrow">▼</div>
                </div>
                <div class="dropdown-menu" *ngIf="genderDropdownOpen">
                  <div
                    class="dropdown-item"
                    *ngFor="let option of genderOptions; trackBy: trackByValue"
                    [class.selected]="selectedGender === option.value"
                    (click)="selectGender(option)"
                  >
                    {{ option.label }}
                  </div>
                </div>
              </div>
              <small *ngIf="isInvalid('gender')" class="error-message">
                Gender selection is required
              </small>
            </div>
          </div>

          <div class="availability-section">
            <div class="availability-item">
              <div class="availability-header">
                <span class="availability-label">Available to Play</span>
                <div class="availability-toggle" (click)="toggleAvailability()">
                  <div
                    class="toggle-slider"
                    [class.active]="profileForm.get('availableToPlay')?.value"
                  >
                    <div class="toggle-handle"></div>
                  </div>
                  <span class="toggle-text">
                    {{
                      profileForm.get("availableToPlay")?.value
                        ? "Available"
                        : "Busy"
                    }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div class="form-actions">
            <button type="button" class="logout-button" (click)="onLogout()">
              <span class="button-icon"
                ><lucide-icon name="log-out" [size]="16"></lucide-icon
              ></span>
              <span class="button-text">Log Out</span>
            </button>
            <button
              type="submit"
              class="save-button"
              [disabled]="profileForm.invalid"
              [class.loading]="profileForm.valid"
            >
              <span class="button-icon"
                ><lucide-icon name="save" [size]="16"></lucide-icon
              ></span>
              <span class="button-text">Save Changes</span>
            </button>
          </div>
        </form>
      </div>

      <div class="danger-zone-container">
        <button
          type="button"
          class="danger-zone-toggle"
          (click)="toggleDangerZone()"
          [class.expanded]="dangerZoneExpanded"
        >
          <span class="toggle-icon">{{ dangerZoneExpanded ? "▼" : "▶" }}</span>
          <span class="toggle-text">Account Management</span>
        </button>

        <div class="danger-zone-content" *ngIf="dangerZoneExpanded">
          <div class="danger-zone-warning">
            <span class="warning-icon"
              ><lucide-icon
                name="alert-triangle"
                [size]="20"
                color="#dc2626"
              ></lucide-icon
            ></span>
            <div class="warning-text">
              <strong>Danger Zone</strong>
              <p>
                Once you delete your account, there is no going back. Please be
                certain.
              </p>
            </div>
          </div>
          <button
            type="button"
            class="delete-account-button"
            (click)="onDeleteAccount()"
          >
            <span class="button-icon"
              ><lucide-icon name="trash-2" [size]="16"></lucide-icon
            ></span>
            <span class="button-text">Delete My Account</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</div>
